import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { transactionAPI } from '../services/api';
import { Search, FileText, Download, Eye } from 'lucide-react';
import { formatDate } from '../utils/formatters';

const Documents = () => {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // 계약 목록 조회
    const { data: transactions, isLoading } = useQuery({
        queryKey: ['transactionsForDocuments', currentPage, searchTerm, selectedStatus],
        queryFn: () => transactionAPI.getTransactions({
            page: currentPage,
            limit: 10,
            search: searchTerm,
            status: selectedStatus
        }),
        keepPreviousData: true,
        select: (data) => {
            // API 응답 구조에 따라 안전하게 처리
            if (data?.data?.transactions) {
                return {
                    data: data.data.transactions,
                    pagination: data.data.pagination
                };
            } else if (data?.data) {
                return {
                    data: Array.isArray(data.data) ? data.data : [],
                    pagination: data.pagination
                };
            } else if (Array.isArray(data)) {
                return {
                    data: data,
                    pagination: null
                };
            }
            return {
                data: [],
                pagination: null
            };
        }
    });

    const handleSearch = () => {
        setCurrentPage(1);
    };

    const getDocumentCount = (transaction) => {
        const documents = transaction.documents || [];
        return documents.length;
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return '활성';
            case 'completed': return '완료';
            case 'cancelled': return '취소';
            default: return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'status-progress';
            case 'completed': return 'status-completed';
            case 'cancelled': return 'status-cancelled';
            default: return 'status-waiting';
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">서류 관리</h1>
            </div>

            {/* 검색 및 필터 */}
            <div className="card mb-6">
                <div className="grid grid-3" style={{ gap: '20px' }}>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            검색
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                type="text"
                                placeholder="사망자명 검색"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            계약 상태
                        </label>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="select w-full"
                        >
                            <option value="">전체</option>
                            <option value="active">활성</option>
                            <option value="completed">완료</option>
                            <option value="cancelled">취소</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={handleSearch}
                            className="btn btn-primary w-full"
                        >
                            조회
                        </button>
                    </div>
                </div>
            </div>

            {/* 서류 목록 테이블 */}
            <div className="card">
                {isLoading ? (
                    <div className="loading">로딩 중...</div>
                ) : transactions?.data?.length === 0 ? (
                    <div className="empty-state">
                        <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                        <p className="text-gray-500">서류가 없습니다.</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="table w-full">
                                <thead>
                                    <tr>
                                        <th>묘적번호</th>
                                        <th>사망자명</th>
                                        <th>연고자명</th>
                                        <th>계약일</th>
                                        <th>상태</th>
                                        <th>서류 수</th>
                                        <th>관리</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions?.data?.map((transaction) => (
                                        <tr key={transaction.id}>
                                            <td className="font-medium">{transaction.mausoleum?.member_number}</td>
                                            <td>{transaction.dead?.[0]?.name}</td>
                                            <td>{transaction.acquaintances?.[0]?.name}</td>
                                            <td>{formatDate(transaction.contract_date)}</td>
                                            <td>
                                                <span className={`status-badge ${getStatusColor(transaction.status)}`}>
                                                    {getStatusText(transaction.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <FileText size={16} className="text-gray-400" />
                                                    <span className="text-sm text-gray-900">
                                                        {getDocumentCount(transaction)}개
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => {/* 서류 보기 로직 */ }}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="서류 보기"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => {/* 서류 다운로드 로직 */ }}
                                                        className="text-green-600 hover:text-green-900"
                                                        title="서류 다운로드"
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* 페이지네이션 */}
                        {transactions?.pagination && (
                            <div className="flex justify-between items-center mt-6">
                                <div className="text-sm text-gray-700">
                                    총 {transactions.pagination.total}개 중 {transactions.pagination.from}-{transactions.pagination.to}개
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="btn btn-secondary"
                                    >
                                        이전
                                    </button>
                                    <span className="px-3 py-2 text-sm text-gray-700">
                                        {currentPage} / {transactions.pagination.last_page}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        disabled={currentPage >= transactions.pagination.last_page}
                                        className="btn btn-secondary"
                                    >
                                        다음
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Documents;